import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';

import { useDevice } from '@bonnet/next/device';

import { pageNames } from '@atc/bonnet-paths';
import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';

import { hideAdClick } from '@/analytics/srp/hideAdEvents';

import { AD_CONFIGS } from '@/config/linerAdSizes';

import srpAdsDuck from '@/ducks/srp/srpAdsDuck.js';
import srpLinerAdDuck from '@/ducks/srp/srpLinerAdDuck.js';

import AtcAdSlot from '@/containers/AtcAdSlot';
import DisableAdsFeatureFlag from '@/containers/global/DisableAdsFeatureFlag';

import HideAdButton from '@/components/HideAdButton';

const { SEARCH_RESULTS } = pageNames;

function useAdConfiguration(index, enabledFluidAd, enableListingCardAd) {
    const device = useDevice();
    const notXs = _get(device, 'greaterThan.xs', false);
    const { brand, brands } = useBrand();
    const isKbb = brands.KBB_BRAND === brand;

    return useMemo(() => {
        if (isKbb) {
            if (enableListingCardAd) {
                const configKey = notXs ? 'desktopListingCard' : 'mobileListingCard';
                return _get(AD_CONFIGS, `kbb.${configKey}.${index}`, null);
            }
            const configKey = notXs ? 'desktop' : 'mobile';
            return _get(AD_CONFIGS, `kbb.${configKey}.${index}`, null);
        }

        if (enableListingCardAd) {
            return _get(AD_CONFIGS, `atc.listingCard.${index}`, null);
        }
        const configKey = enabledFluidAd ? 'fluid' : 'default';
        return _get(AD_CONFIGS, `atc.${configKey}.${index}`, null);
    }, [index, isKbb, notXs, enabledFluidAd, enableListingCardAd]);
}

function ListingsLinerAds({ index }) {
    const {
        fluid_ad: [enabledFluidAd],
        listing_card_ad: [enableListingCardAd],
        enable_hide_ad: [enableHideAd],
    } = useFeatures(['fluid_ad', 'listing_card_ad', 'enable_hide_ad']);

    const dispatch = useDispatch();
    const selectorKey = `getHideLinerAd${index ?? 0}`;
    const hideLinerAd = useSelector(
        srpLinerAdDuck.selectors[selectorKey] || (() => false)
    );
    const adsLazyLoadingOffset = useSelector(srpAdsDuck.selectors.getAdsLazyLoadingOffset);

    const device = useDevice();
    const notXs = _get(device, 'greaterThan.xs', false);

    const adProps = useAdConfiguration(index, enabledFluidAd, enableListingCardAd);

    const handleHideLinerAd = useCallback(async (event) => {
        const eventTarget = _get(event, 'target.value', null);
        await interactionResponse();
        _set(event, 'target.value', eventTarget);

        const adTypeName = enabledFluidAd ? 'Fluid Ad' : 'Liner Ad';
        sendClick(hideAdClick, event, { adType: `${adTypeName} - ${index + 1}` });
        dispatch(srpLinerAdDuck.creators.setKeys({ [`hideLinerAd${index}`]: true }));
    }, [dispatch, index, enabledFluidAd]);

    if (!adProps || (enableHideAd && hideLinerAd)) {
        return null;
    }

    const sizes = notXs ? adProps.sizes : adProps.xsSizes;

    return (
        <DisableAdsFeatureFlag>
            <AtcAdSlot
                className={clsx('display-flex flex-column', {
                    'justify-content-center margin-bottom-5': !enabledFluidAd && adProps.position !== 'testvert',
                })}
                lazyLoading
                lazyLoadingBottomOffset={adsLazyLoadingOffset}
                lazyLoadingTopOffset={adsLazyLoadingOffset}
                preserveSpace={!notXs || !enabledFluidAd}
                showPlaceholder
                size={sizes}
                slotId={adProps.id}
                targeting={{ pos: adProps.position, prd: 'rc12' }}
                adSelector={srpAdsDuck.selectors.getDuckState}
                pageName={SEARCH_RESULTS}
                advertisementLabel={{
                    className: 'display-flex justify-content-end',
                    text: 'Advertisement',
                    enableHide: enableHideAd,
                    hideButton: HideAdButton,
                    onClickHide: handleHideLinerAd,
                }}
            />
        </DisableAdsFeatureFlag>
    );
}

export default ListingsLinerAds;
